import { defineStore, DefineStoreOptions } from 'pinia';
// import { UserModel } from '@core/models';
import { useCommonStore } from '@stores';
import { jwtDecode } from 'jwt-decode';
import { SignUpResponse, TgMembership } from '@core/dto';
import { LUNAR_CODE, STATUS_YN, GENDER_CODE } from '@core/enums';
import { AxiosAdapter, UserAdapter } from '@adapters';
import { FetchUserInfoUseCase } from '@core/useCases';

export const fetchUserData = async () => {
  const axiosAdapter = new AxiosAdapter();
  const userAdapter = new UserAdapter(axiosAdapter);
  const fetchUserInfoUseCase = new FetchUserInfoUseCase(userAdapter);
  return await fetchUserInfoUseCase.execute();
};

interface UserInfoForMember {
  memberId: string;
  cprDiv: string;
  memberName: string;
  hpNo: string;
  sexCode: GENDER_CODE;
  birthDay: string;
  lunarCode: LUNAR_CODE;
  smsRecptnDiv: STATUS_YN;
}
interface UserStoreState {
  // userInfo: UserModel;
  accessToken: string | null;
  isPrivacyUseAgreement: boolean;
  isPhoneCertification: boolean;
  userInfo: SignUpResponse | null;
  userInfoForMember: UserInfoForMember | null;
  memberName: string;
  hpNo: string;
  memberId: string;
  membershipList: TgMembership[] | [];
  cprDiv: string;
  isMarketingAgree: boolean;
  isRegularMember: boolean;
  userIdForSignupComplete: string;
  userInfoFromOAuth: any | null;
}
export interface HomepageAPIStoreState {
  accessToken: string | null;
}
interface UserStoreGetters {
  // getUserInfo(): UserModel;
  userId: (state: HomepageAPIStoreState) => string | null | undefined;
  username: (state: HomepageAPIStoreState) => string | null | undefined;
  marketingAgree: (state: UserStoreState) => boolean;
  grant: (state: UserStoreState) => string | null | undefined;
  currentMembership: (state: UserStoreState) => TgMembership | null;
}
interface UserStoreActions {
  setPhoneCertification(isCertification: boolean): void;
  setUserInfo(userInfo: SignUpResponse): void;
  setUserIdForSignupComplete(userId: string): void;
  setMemberName(userName: string): void;
  setHpNo(phone: string): void;
  setMemberId(memberId: string): void;
  setCprDiv(cprDiv: string): void;
  setPrivacyUseAgreement(isAgree: boolean): void;
  resetState(): void;
  resetUser(): void;
  setUser(token: string | null): void;
  setMarketingAgree(isAgree: boolean): void;
  setRegularMember(isRegularMember: boolean): void;
  setUserInfoFromOAuth(userInfo: any): void;
  setUserInfoForMember(userInfo: UserInfoForMember): void;
  fetchMembershipList(): void;
  resetUserInfo(): void;
}

interface AccessTokenDecoded {
  'com.fixelsoft.auth.jwt.auth.userId': string;
  'com.fixelsoft.auth.jwt.auth.username': string;
  'com.fixelsoft.auth.jwt.auth.grant': string;
  'com.fixelsoft.auth.jwt.auth.webId'?: string;
  'com.fixelsoft.auth.jwt.auth.memberId'?: string;
  'com.fixelsoft.auth.jwt.auth.membershipIds'?: string[];
  'com.fixelsoft.auth.jwt.auth.memberName'?: string;
}

type UserStoreOptions = DefineStoreOptions<
  string,
  UserStoreState,
  UserStoreGetters,
  UserStoreActions
>;

const getAccessToken = (): string | null => {
  const commonStore = useCommonStore();
  return commonStore.getSessionStorage('token');
};

const userStoreId = 'user';
const userStoreOptions: UserStoreOptions = {
  id: userStoreId,
  state() {
    return {
      accessToken: null,
      isPrivacyUseAgreement: true,
      isPhoneCertification: false,
      userInfo: null,
      memberName: '',
      hpNo: '',
      memberId: '',
      membershipList: [],
      cprDiv: '',
      isMarketingAgree: true,
      isRegularMember: false,
      userIdForSignupComplete: '',
      userInfoFromOAuth: null,
      userInfoForMember: null,
    };
  },
  getters: {
    userId: ({ accessToken }) => {
      const token = accessToken || getAccessToken();
      if (token) {
        return (
          jwtDecode<AccessTokenDecoded>(token)[
            'com.fixelsoft.auth.jwt.auth.userId'
          ] || null
        );
      } else {
        return null;
      }
    },
    username: ({ accessToken }) => {
      const token = accessToken || getAccessToken();
      if (token) {
        return (
          jwtDecode<AccessTokenDecoded>(token)[
            'com.fixelsoft.auth.jwt.auth.username'
          ] || null
        );
      } else {
        return null;
      }
    },
    grant: () => {
      const { accessToken } = useCommonStore();
      const token = accessToken || getAccessToken();
      const grant = token
        ? jwtDecode<AccessTokenDecoded>(token)[
            'com.fixelsoft.auth.jwt.auth.grant'
          ]
        : null;
      return grant || null;
    },
    marketingAgree: ({ isMarketingAgree }) => isMarketingAgree,
    membershipIds: ({ accessToken }) => {
      const token = accessToken || getAccessToken();
      if (token) {
        return (
          jwtDecode<AccessTokenDecoded>(token)[
            'com.fixelsoft.auth.jwt.auth.membershipIds'
          ] || []
        );
      } else {
        return [];
      }
    },
    currentMembership: ({ membershipList }) => {
      const commonStore = useCommonStore();
      return (
        membershipList.find(
          (f) => f.membershipId === commonStore.membershipId
        ) || null
      );
    },
  },
  actions: {
    setPrivacyUseAgreement(isAgree: boolean) {
      this.isPrivacyUseAgreement = isAgree;
    },
    setPhoneCertification(isCertification: boolean) {
      this.isPhoneCertification = isCertification;
    },
    setUserInfo(userInfo: SignUpResponse) {
      this.userInfo = userInfo;
    },
    setMemberName(memberName: string) {
      this.memberName = memberName;
    },
    setHpNo(hpNo: string) {
      this.hpNo = hpNo;
    },
    setMemberId(memberId: string) {
      this.memberId = memberId;
    },
    setCprDiv(cprDiv: string) {
      this.cprDiv = cprDiv;
    },
    resetState() {
      this.isPrivacyUseAgreement = false;
      this.isPhoneCertification = false;
    },
    resetUser() {
      this.accessToken = null;
    },
    setUser(token) {
      this.accessToken = token;
    },
    setMarketingAgree(isAgree: boolean) {
      this.isMarketingAgree = isAgree;
    },
    setRegularMember(isRegularMember: boolean) {
      this.isRegularMember = isRegularMember;
    },
    setUserIdForSignupComplete(userId: string) {
      this.userIdForSignupComplete = userId;
    },
    setUserInfoFromOAuth(userInfo: any) {
      this.userInfoFromOAuth = {
        accessToken: userInfo.accessToken,
        memberName: userInfo.tgWebId.tgMember.memberName,
        birthDay: userInfo.tgWebId.tgMember.birthDay,
        hpNo: userInfo.tgWebId.tgMember.hpNo,
        sexCode: userInfo.tgWebId.tgMember.sexCode,
        email: userInfo.tgWebId.tgMember.tgMemberAddi.email,
      };
    },
    setUserInfoForMember(userInfo: UserInfoForMember) {
      this.userInfoForMember = userInfo;
    },
    async fetchMembershipList() {
      const {
        tgMember: { tgMemberships },
      } = await fetchUserData();
      this.membershipList = tgMemberships;
    },
    resetUserInfo() {
      this.membershipList = [];
    },
  },
};

export const useUserStore = defineStore(userStoreId, userStoreOptions);
